// Offline-first store for the FieldClaim field app.
// IndexedDB (via idb) holds the org's synced data + a queue of local mutations.
// Idempotency: every locally-created record carries a stable `client_uuid` so the
// backend upserts safely on retry (offline sync surface).
import { openDB } from "idb";
import api from "@/lib/api";
import { compressImage, stampWatermark } from "@/lib/image";

const DB_NAME = "mdau-field";
const DB_VERSION = 1;
const STORES = ["claims", "assets", "photos", "runs", "alerts", "swms", "ops"];

export const uuid = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
export const localId = () => `tmp_${uuid()}`;
const isLocal = (id) => typeof id === "string" && id.startsWith("tmp_");

let _db;
async function db() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(d) {
      d.createObjectStore("kv", { keyPath: "k" });
      for (const s of STORES) d.createObjectStore(s, { keyPath: "id" });
    },
  });
  return _db;
}

async function kvGet(k, dflt = null) {
  const r = await (await db()).get("kv", k);
  return r ? r.v : dflt;
}
async function kvSet(k, v) {
  await (await db()).put("kv", { k, v });
}

export async function all(store) {
  return (await db()).getAll(store);
}
export async function getRec(store, id) {
  return (await db()).get(store, id);
}
async function putRec(store, rec) {
  await (await db()).put(store, rec);
  return rec;
}

export const getOrg = () => kvGet("org");
export const getMeta = () => kvGet("meta");
export const getLastSync = () => kvGet("last_sync");

// ---- getters (all local, offline-safe) -----------------------------------
export async function getClaims() {
  const c = await all("claims");
  return c.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
}
export async function getAssets(claimId) {
  const a = await all("assets");
  return a
    .filter((x) => x.claim_id === claimId)
    .sort((x, y) => String(x.created_at || "").localeCompare(String(y.created_at || "")));
}
export async function getPhotos(assetId) {
  const p = await all("photos");
  return p
    .filter((x) => x.asset_id === assetId)
    .sort((x, y) => String(x.created_at || "").localeCompare(String(y.created_at || "")));
}
export async function getRuns(claimId) {
  const r = await all("runs");
  return (claimId ? r.filter((x) => x.claim_id === claimId) : r).sort((a, b) =>
    String(b.created_at || "").localeCompare(String(a.created_at || ""))
  );
}
export const getRun = (id) => getRec("runs", id);
export const getSwms = () => all("swms");
export const getAlerts = () => all("alerts");

// ---- local mutations (queue) ----------------------------------------------
export async function queueAsset(claimId, payload) {
  const cu = uuid();
  const rec = {
    id: localId(),
    client_uuid: cu,
    claim_id: claimId,
    ...payload,
    payload: { ...payload, client_uuid: cu },
    synced: false,
    created_at: new Date().toISOString(),
  };
  return putRec("assets", rec);
}

export async function queuePhoto(assetId, claimId, phase, file, meta = {}) {
  const compressed = await compressImage(file);
  const blob = await stampWatermark(compressed, {
    phase,
    latitude: meta.latitude,
    longitude: meta.longitude,
    gps_accuracy: meta.gps_accuracy,
    bearing: meta.bearing,
  });
  const cu = uuid();
  const rec = {
    id: localId(),
    client_uuid: cu,
    asset_id: assetId,
    claim_id: claimId,
    phase,
    blob,
    hasBlob: true,
    filename: (file.name || "photo.jpg").replace(/\.[^.]+$/, "") + ".jpg",
    latitude: meta.latitude ?? null,
    longitude: meta.longitude ?? null,
    gps_accuracy: meta.gps_accuracy ?? null,
    bearing: meta.bearing ?? null,
    chainage: meta.chainage || null,
    note: meta.note || null,
    taken_at: new Date().toISOString(),
    synced: false,
    created_at: new Date().toISOString(),
  };
  return putRec("photos", rec);
}

export async function deletePhoto(id) {
  const d = await db();
  const p = await d.get("photos", id);
  if (p && p.synced && !isLocal(p.id)) {
    // queue a server delete; drop locally regardless
    await queueOp("photo_delete", { photo_id: p.id });
  }
  await d.delete("photos", id);
}

export async function queueOp(kind, payload) {
  return putRec("ops", { id: localId(), kind, payload, synced: false, created_at: new Date().toISOString() });
}

// ---- pending / sync --------------------------------------------------------
export async function pendingCount() {
  const [a, p, o] = await Promise.all([all("assets"), all("photos"), all("ops")]);
  return (
    a.filter((x) => x.synced === false).length +
    p.filter((x) => x.synced === false).length +
    o.filter((x) => x.synced === false).length
  );
}

export async function pendingList() {
  const [a, p, o] = await Promise.all([all("assets"), all("photos"), all("ops")]);
  return {
    assets: a.filter((x) => x.synced === false),
    photos: p.filter((x) => x.synced === false),
    ops: o.filter((x) => x.synced === false),
  };
}

async function runOp(op) {
  const p = op.payload || {};
  if (op.kind === "swms_ack") await api.post("/fieldclaim/swms/ack", p);
  else if (op.kind === "run_visit")
    await api.patch(`/fieldclaim/runs/${p.run_id}`, { visited_asset_id: p.asset_id, visited: p.visited !== false });
  else if (op.kind === "asset_update") await api.patch(`/fieldclaim/assets/${p.asset_id}`, p.updates);
  else if (op.kind === "claim_locate")
    await api.patch(`/fieldclaim/claims/${p.claim_id}`, { latitude: p.latitude, longitude: p.longitude });
  else if (op.kind === "photo_delete") await api.delete(`/fieldclaim/photos/${p.photo_id}`);
}

export async function push() {
  const d = await db();

  // 1) local assets → server (idempotent via client_uuid); remap tmp id → server id
  const assets = (await d.getAll("assets")).filter((x) => x.synced === false);
  for (const a of assets) {
    if (isLocal(a.claim_id)) continue; // parent event not on server yet
    try {
      const { data: srv } = await api.post(`/fieldclaim/claims/${a.claim_id}/assets`, a.payload);
      await d.delete("assets", a.id);
      await d.put("assets", { ...srv, synced: true });
      // repoint any photos captured against the temp asset id
      const photos = await d.getAll("photos");
      const tx = d.transaction("photos", "readwrite");
      for (const ph of photos) if (ph.asset_id === a.id) await tx.store.put({ ...ph, asset_id: srv.id });
      await tx.done;
    } catch (e) {
      /* stays queued */
    }
  }

  // 2) local photos → server (skip those whose asset is still local)
  const photos = (await d.getAll("photos")).filter((x) => x.synced === false && x.blob);
  for (const p of photos) {
    if (isLocal(p.asset_id)) continue;
    try {
      const fd = new FormData();
      fd.append("file", p.blob, p.filename || "photo.jpg");
      fd.append("phase", p.phase);
      ["latitude", "longitude", "gps_accuracy", "bearing", "chainage", "note", "taken_at"].forEach((k) => {
        if (p[k] != null && p[k] !== "") fd.append(k, p[k]);
      });
      fd.append("client_uuid", p.client_uuid);
      const { data: srv } = await api.post(`/fieldclaim/assets/${p.asset_id}/photos`, fd);
      await d.delete("photos", p.id);
      await d.put("photos", { ...srv, blob: null, hasBlob: false, synced: true });
    } catch (e) {
      /* stays queued */
    }
  }

  // 3) fire-and-forget ops (acks, visits, edits, locates, deletes)
  const ops = (await d.getAll("ops")).filter((x) => x.synced === false);
  for (const op of ops) {
    try {
      await runOp(op);
      await d.delete("ops", op.id);
    } catch (e) {
      /* stays queued */
    }
  }
}

async function merge(store, items) {
  const d = await db();
  const tx = d.transaction(store, "readwrite");
  for (const it of items) {
    const ex = await tx.store.get(it.id);
    if (ex && ex.synced === false) continue; // never clobber an unsynced local edit
    await tx.store.put({ ...it, synced: true });
  }
  await tx.done;
}

export async function pull() {
  const since = await getLastSync();
  const { data } = await api.get(`/fieldclaim/sync${since ? `?since=${encodeURIComponent(since)}` : ""}`);
  await merge("claims", data.claims || []);
  await merge("assets", data.assets || []);
  await merge("runs", data.runs || []);
  await merge("alerts", (data.alerts || []).map((a) => ({ ...a })));

  // photo metadata (no binaries) — keep any local pending capture intact
  const d = await db();
  const tx = d.transaction("photos", "readwrite");
  for (const p of data.photos || []) {
    const ex = await tx.store.get(p.id);
    if (ex && ex.synced === false) continue;
    await tx.store.put({ ...p, blob: null, hasBlob: false, synced: true });
  }
  await tx.done;

  try {
    const sw = await api.get("/fieldclaim/swms");
    const t2 = d.transaction("swms", "readwrite");
    await t2.store.clear();
    for (const s of sw.data || []) await t2.store.put({ ...s });
    await t2.done;
  } catch (e) {
    /* offline: keep cached swms */
  }

  if (data.org) await kvSet("org", data.org);
  await kvSet("last_sync", data.server_time);
  return data;
}

export async function refreshMeta() {
  const { data } = await api.get("/fieldclaim/meta");
  await kvSet("meta", data);
  return data;
}

export async function editAsset(id, fields) {
  const d = await db();
  const a = await d.get("assets", id);
  if (!a) return;
  const merged = { ...a, ...fields };
  if (a.synced === false) merged.payload = { ...a.payload, ...fields };
  await d.put("assets", merged);
  if (a.synced !== false && !isLocal(a.id)) await queueOp("asset_update", { asset_id: a.id, updates: fields });
}

export async function locateClaim(claimId, latitude, longitude) {
  const d = await db();
  const c = await d.get("claims", claimId);
  if (c) await d.put("claims", { ...c, latitude, longitude });
  await queueOp("claim_locate", { claim_id: claimId, latitude, longitude });
}

export async function visitStop(runId, assetId, visited = true) {
  const d = await db();
  const r = await d.get("runs", runId);
  if (r && Array.isArray(r.stops)) {
    r.stops = r.stops.map((s) => (s.asset_id === assetId ? { ...s, visited } : s));
    if (r.stops.length && r.stops.every((s) => s.visited)) r.status = "done";
    else if (r.status === "planned") r.status = "in_progress";
    await d.put("runs", r);
  }
  await queueOp("run_visit", { run_id: runId, asset_id: assetId, visited });
}

export async function joinOrg(code) {
  const { data } = await api.post("/fieldclaim/org/join", { code: (code || "").trim().toUpperCase() });
  // switching org: reset local caches so we don't mix data
  await kvSet("last_sync", null);
  const d = await db();
  for (const s of STORES) {
    const tx = d.transaction(s, "readwrite");
    await tx.store.clear();
    await tx.done;
  }
  return data;
}
